import { useState } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import { logAuditEvent } from '../../../shared/lib/auditLog'
import MultiSelectDropdown from '../../../shared/components/ui/MultiSelectDropdown'

function InviteClientUserModal({ clients, currentOrg, onClose, onSuccess }) {
  const [userType, setUserType] = useState('client') // 'client' | 'org'
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [selectedClientId, setSelectedClientId] = useState('')
  const [selectedClientIds, setSelectedClientIds] = useState([])
  const [orgRole, setOrgRole] = useState('org_member')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [createdUser, setCreatedUser] = useState(null) // { email }

  const handleDone = () => {
    onSuccess?.()
    onClose()
  }

  const clientOptions = (clients || []).map(c => ({ value: c.id, label: c.name }))

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!email.trim()) {
      setError('Please enter an email address')
      return
    }

    if (userType === 'client' && !selectedClientId) {
      setError('Please select a client to assign this user to')
      return
    }

    if (userType === 'org' && orgRole === 'org_member' && selectedClientIds.length === 0) {
      setError('Please select at least one client to assign this member to')
      return
    }

    setLoading(true)
    setError('')

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('No active session')

      if (userType === 'client') {
        const response = await fetch('/api/users', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: email.trim(),
            fullName: fullName.trim(),
            clientId: selectedClientId
          })
        })

        const data = await response.json()
        if (!response.ok) throw new Error(data.error || 'Failed to create user')

        const selectedClient = clients.find(c => c.id === selectedClientId)
        logAuditEvent({
          category: 'change',
          action: 'user.created',
          description: `Created client user "${email.trim()}" for "${selectedClient?.name || 'client'}"`,
          clientId: selectedClientId,
          clientName: selectedClient?.name,
          resourceType: 'user',
          metadata: { email: email.trim(), fullName: fullName.trim(), userType: 'client' }
        })
      } else {
        if (!currentOrg?.id) throw new Error('No organization found')

        const assignedClientIds = orgRole === 'org_admin'
          ? clients.map(c => c.id)
          : selectedClientIds

        const response = await fetch(`/api/organizations/${currentOrg.id}/members`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: email.trim(),
            fullName: fullName.trim(),
            role: orgRole,
            clientIds: assignedClientIds
          })
        })

        const data = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(data.error || 'Failed to invite organization member')

        const clientLabel = orgRole === 'org_admin'
          ? 'all clients'
          : `${assignedClientIds.length} client${assignedClientIds.length !== 1 ? 's' : ''}`

        logAuditEvent({
          category: 'change',
          action: 'user.created',
          description: `Created org ${orgRole === 'org_admin' ? 'admin' : 'member'} "${email.trim()}" for "${currentOrg.name || 'organization'}" (${clientLabel})`,
          resourceType: 'user',
          metadata: { email: email.trim(), fullName: fullName.trim(), userType: 'org', orgRole, clientIds: assignedClientIds }
        })
      }

      // Show confirmation panel instead of closing
      setCreatedUser({ email: email.trim() })
    } catch (err) {
      console.error('Error inviting user:', err)
      setError(err.message || 'Failed to invite user')
    } finally {
      setLoading(false)
    }
  }

  // Determine which client selector to show
  const showSingleClientSelect = userType === 'client'
  const showMultiClientSelect = userType === 'org' && orgRole === 'org_member'


  // Confirmation panel (shown after successful user invite)
  if (createdUser) {
    const welcomeUrl = `${window.location.origin}/welcome?email=${encodeURIComponent(createdUser.email)}`
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Invitation Sent</h2>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
            An invitation email has been sent to <span className="font-semibold text-gray-900 dark:text-white">{createdUser.email}</span>. The user will click the invitation link, request a 6-digit sign-in code on the welcome page, enter the code, then be prompted to set a password and enroll in MFA.
          </p>

          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Welcome URL (fallback)
            </label>
            <a href={welcomeUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-mono text-primary-600 dark:text-primary-400 hover:underline break-all">
              {welcomeUrl}
            </a>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
              If the email doesn't arrive, share this URL with the user directly.
            </p>
          </div>

          <div className="mb-5 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
            <p className="font-semibold mb-1">What the user will see</p>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>An invitation email with a welcome link</li>
              <li>The welcome page asking them to confirm their email and request a code</li>
              <li>A second email with a 6-digit sign-in code</li>
              <li>Enter the code, set a password, enroll in MFA</li>
            </ol>
          </div>

          <div className="flex justify-end">
            <button onClick={handleDone} className="btn-primary">
              Done
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Invite User</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-800 dark:text-blue-300">
          An invitation email will be sent. The user will request a 6-digit sign-in code on the welcome page, then set a password and enroll in MFA.
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="user@example.com"
              required
            />
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Full Name
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="John Doe"
            />
          </div>

          {/* User Type Dropdown */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              User Type <span className="text-red-500">*</span>
              <span className="relative group">
                <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="invisible group-hover:visible absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2.5 text-xs font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg">
                  <span className="font-semibold">Client Portal User</span> — Can only access their assigned client's portal. Must set a password and enroll in MFA on first login.
                  <br /><br />
                  <span className="font-semibold">Organization Team</span> — Access the admin portal as a Member (limited client visibility) or Org Admin (full access).
                  <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></span>
                </span>
              </span>
            </label>
            <select
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            >
              <option value="client">Client Portal User</option>
              <option value="org">Organization Team</option>
            </select>
          </div>

          {/* Org Role (org users only) */}
          {userType === 'org' && (
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Organization Role <span className="text-red-500">*</span>
                <span className="relative group">
                  <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="invisible group-hover:visible absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2.5 text-xs font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg">
                    <span className="font-semibold">Member</span> — Can view and manage only their assigned clients.
                    <br /><br />
                    <span className="font-semibold">Org Admin</span> — Full access to all clients and organization settings.
                    <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></span>
                  </span>
                </span>
              </label>
              <select
                value={orgRole}
                onChange={(e) => setOrgRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="org_member">Member</option>
                <option value="org_admin">Org Admin</option>
              </select>
            </div>
          )}

          {/* Single client select (client portal users) */}
          {showSingleClientSelect && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Assign to Client <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                <option value="">Select a client...</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Multi-client select (org member) */}
          {showMultiClientSelect && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Assign to Clients <span className="text-red-500">*</span>
              </label>
              <MultiSelectDropdown
                options={clientOptions}
                selectedValues={selectedClientIds}
                onChange={setSelectedClientIds}
                placeholder="Select clients..."
                searchPlaceholder="Search clients..."
              />
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Sending invite...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default InviteClientUserModal
